import {
  Button,
  Checkbox,
  Chips,
  Css,
  GridColumn,
  GridDataRow,
  GridTable,
  IconButton,
  Palette,
  RightPaneLayout,
  RowStyles,
  ScrollableContent,
  TabsWithContent,
  Tooltip,
  collapseColumn,
  column,
  emptyCell,
  numericColumn,
  simpleHeader,
  useModal,
} from "@homebound/beam";
import xor from "lodash/xor";
import { useMemo, useState } from "react";
import { Loading, Price, priceCell } from "src/components";
import { useFullscreenModal } from "src/components/useFullscreenModal";
import { BidContractType, BidPackageStatus, Maybe, TakeoffLineItemVersion } from "src/generated/graphqlTypes";
import {
  BidPackageDetailActiveRequestFragment,
  PageBidPackageDetailFragment,
} from "src/routes/bids/BidPackageDetailPage.generated";
import { DropCodeCell } from "src/routes/developments/product-offerings/components/DropCodeCell";
import { AggregateMemberVersionChangeType } from "src/routes/libraries/design-catalog/design-package-configurator/components/DesignUpdatesAvailableButton";
import { fail, isDefined, isNumber, pluralize, queryResult } from "src/utils";
import { StringParam, useQueryParams } from "use-query-params";
import { getOptionsKey } from "./BidsTab";
import { TradeCostData, sumTradeCostData } from "./BidsTab.utils";
import { ImportBidModal } from "./ImportBidModal";
import {
  PlanBasedBidsTable_AggregateVersionsQuery,
  PlanBasedBidsTable_BidPackageVersionFragment,
  PlanBasedBidsTable_PreviousTakeoffLineItemVersionFragment,
  PlanBasedBidsTable_ReadyPlanOptionFragment,
  PlanBasedBidsTable_ScopeLinesFragment,
  PlanBasedBidsTable_TakeoffLineItemVersionFragment,
  usePlanBasedBidsTable_AggregateVersionsQuery,
  usePlanBasedBidsTable_ScopeLinesQuery,
} from "./PlanBasedBidsTable.generated";
import { AddProrationBidModal, EditProrationBidModal } from "./ProrateBidModal";
import { SelectProrationModal } from "./SelectProrationModal";
import { getBidPackageTradesWithEstimate } from "./utils";

type PlanBasedBidsTableProps = {
  bidPackage: PageBidPackageDetailFragment;
  searchFilter?: string;
  requestsWithEditableBids: BidPackageDetailActiveRequestFragment[];
};

export function PlanBasedBidsTable({ bidPackage, searchFilter, requestsWithEditableBids }: PlanBasedBidsTableProps) {
  const [{ versionId }] = useQueryParams({ versionId: StringParam });

  const query = usePlanBasedBidsTable_AggregateVersionsQuery({
    variables: {
      bidPackageVersionId: versionId ?? bidPackage.latestVersion.id,
    },
  });

  return queryResult(query, ({ bidPackageVersion }) => (
    <PlanBasedBidsView
      bidPackage={bidPackage}
      searchFilter={searchFilter}
      requestsWithEditableBids={requestsWithEditableBids}
      bidPackageVersion={bidPackageVersion}
    />
  ));
}

type PlanBasedBidsViewProps = PlanBasedBidsTableProps & {
  bidPackageVersion: PlanBasedBidsTable_AggregateVersionsQuery["bidPackageVersion"];
};

function PlanBasedBidsView({ bidPackageVersion, ...props }: PlanBasedBidsViewProps) {
  const sortedRpavs = bidPackageVersion.readyPlanAggregateVersions.sortBy((rpav) => rpav.readyPlan.id);
  const [selectedTab, setSelectedTab] = useState<string>(sortedRpavs[0].readyPlan.id);
  const [{ versionId }] = useQueryParams({
    versionId: StringParam,
  });
  const query = usePlanBasedBidsTable_ScopeLinesQuery({
    variables: {
      bidPackageVersionId: bidPackageVersion.id,
      highlightChanges: props.bidPackage.status.code === BidPackageStatus.Draft && !isDefined(versionId),
      filter: { readyPlan: [selectedTab] },
    },
  });
  return (
    <TabsWithContent
      alwaysShowAllTabs
      tabs={sortedRpavs.map((rpav) => ({
        name: rpav.displayName,
        value: rpav.readyPlan.id,
        render: () => {
          // When this tab is shown, we'll be using the `readyPlan: selectedTab` to pull back only this plan's rows
          if (query.loading) return <Loading />;
          return queryResult(query, ({ bidPackageVersion, bidPackageVersionScopeLines }) => (
            <ScrollableContent virtualized>
              <RightPaneLayout>
                <div css={Css.h100.$}>
                  <PlanBasedBidsTableView
                    {...props}
                    bidPackageVersion={bidPackageVersion}
                    scopeLines={bidPackageVersionScopeLines}
                  />
                </div>
              </RightPaneLayout>
            </ScrollableContent>
          ));
        },
      }))}
      selected={selectedTab}
      onChange={setSelectedTab}
    />
  );
}

type PlanBasedBidsTableViewProps = PlanBasedBidsTableProps & {
  bidPackageVersion: PlanBasedBidsTable_BidPackageVersionFragment;
  scopeLines: PlanBasedBidsTable_ScopeLinesFragment[];
};

function PlanBasedBidsTableView({
  bidPackage,
  searchFilter,
  requestsWithEditableBids,
  bidPackageVersion,
  scopeLines,
}: PlanBasedBidsTableViewProps) {
  const { openModal } = useModal();
  const { openFullscreen } = useFullscreenModal();
  const displayingOldVersion = bidPackageVersion.id !== bidPackage.latestVersion.id;
  const [onlyChanged, setOnlyChanged] = useState(false);

  const tradePartnerIdsWithRevisions = bidPackage.latestVersion.latestBidContractRevisions
    .map((bcr) => bcr.bidContract.tradePartner?.id)
    .compact();
  const requestsWithoutContracts = bidPackage.activeRequests.filter(
    (r) => !tradePartnerIdsWithRevisions.includes(r.tradePartner.id),
  );
  const isDraft = bidPackage.status.code === BidPackageStatus.Draft;

  function onCreateBid() {
    if (bidPackage.isUnitBased) {
      openModal({
        content: (
          <ImportBidModal
            requests={requestsWithEditableBids}
            bidPackageName={bidPackage.name}
            latestBidContractRevisions={bidPackage.latestVersion.latestBidContractRevisions}
            bidPackageVersionId={bidPackage.latestVersion.id}
          />
        ),
      });
    } else {
      openModal({
        content: (
          <SelectProrationModal
            requests={requestsWithoutContracts}
            bidPackageName={bidPackage.name}
            onContinue={(tradePartnerId, type) => {
              if (bidPackage.isInternalEstimate) {
                return openFullscreen(
                  <AddProrationBidModal
                    newBidContractParams={{
                      internalEstimateBidPackageId: bidPackage.id,
                      isInternalEstimate: true,
                      type,
                    }}
                    newBidContractRevisionParams={
                      bidPackage.internalEstimateBidContract
                        ? {
                            bidContractId: bidPackage.internalEstimateBidContract.id,
                            bidPackageVersionId: bidPackage.latestVersion.id,
                          }
                        : undefined
                    }
                    scopeLineItems={scopeLines.map((sl) => sl.takeoffLineItemVersion)}
                    tradePartnerName="Homebound"
                  />,
                );
              }
              const request = requestsWithoutContracts.find((r) => r.tradePartner.id === tradePartnerId);
              if (!request) fail("Unable to find request");
              openFullscreen(
                <AddProrationBidModal
                  newBidContractParams={{
                    bidRequestId: request.id,
                    tradePartnerId,
                    type,
                  }}
                  newBidContractRevisionParams={
                    request.bidContract
                      ? { bidContractId: request.bidContract.id, bidPackageVersionId: bidPackage.latestVersion.id }
                      : undefined
                  }
                  scopeLineItems={scopeLines.map((sl) => sl.takeoffLineItemVersion)}
                  tradePartnerName={request.tradePartner.name}
                />,
              );
            }}
            isInternalEstimate={bidPackage.isInternalEstimate}
          />
        ),
      });
    }
  }

  function onEditProratedBid(bidContractRevisionId: string) {
    openFullscreen(
      <EditProrationBidModal
        bidContractRevisionId={bidContractRevisionId}
        scopeLineItems={scopeLines.map((sl) => sl.takeoffLineItemVersion)}
      />,
    );
  }

  function onUploadProratedBid(tradePartnerId: string) {
    openModal({
      content: (
        <ImportBidModal
          requests={bidPackage.activeRequests.filter((r) => r.tradePartner.id === tradePartnerId)}
          bidPackageName={bidPackage.name}
          latestBidContractRevisions={bidPackage.latestVersion.latestBidContractRevisions}
          bidPackageVersionId={bidPackage.latestVersion.id}
          isUnitBased={bidPackage.isUnitBased}
          tradePartnerId={tradePartnerId}
        />
      ),
    });
  }

  const rows = useMemo(() => {
    const allRows = createRows(bidPackage, bidPackageVersion, isDraft, scopeLines);
    if (!onlyChanged) return allRows;
    return filterToChangedRows(allRows);
  }, [bidPackage, bidPackageVersion, scopeLines, isDraft, onlyChanged]);

  const columns = useMemo(() => {
    // Instead of showing all options with a location, we want to show options that share a global option in different locations
    const optionsWithMultipleGlobalOptions = scopeLines
      .flatMap((scopeLine) => scopeLine.takeoffLineItemVersion.options)
      .unique()
      .groupBy((o) => o.globalOption.id)
      .toValues()
      .filter((options) => options.length > 1)
      .flatMap((o) => o.map((o) => o.id));
    return createColumns(
      bidPackage,
      onEditProratedBid,
      onUploadProratedBid,
      isDraft,
      displayingOldVersion,
      optionsWithMultipleGlobalOptions,
    );
  }, [bidPackage, isDraft, onEditProratedBid, onUploadProratedBid, displayingOldVersion, scopeLines]);

  const optionTypesGrouped = scopeLines
    .flatMap((scopeLine) => scopeLine.takeoffLineItemVersion.options)
    .unique()
    .groupBy((o) => o.type.id);
  const optionTypesCount = Object.entries(optionTypesGrouped).map(([, options]) => ({
    optionType: options[0].type.name,
    count: options.length,
  }));

  return (
    <>
      <div css={Css.df.w100.aic.gap2.py3.p2.$}>
        <div css={Css.xl.$}>Bid Total</div>
        {<Chips values={optionTypesCount.map(({ count, optionType }) => `${count} ${pluralize(count, optionType)}`)} />}
        {isDraft && <Checkbox label="Only Changed" selected={onlyChanged} onChange={setOnlyChanged} />}
        {!isDraft && !displayingOldVersion && (
          <div css={Css.mla.$}>
            <Button
              variant="text"
              label={bidPackage.isUnitBased ? "Upload Bid" : "Add Bid"}
              disabled={
                (bidPackage.isInternalEstimate &&
                  bidPackage.isUnitBased &&
                  "Currently not supported for Internal Estimates") ||
                ((bidPackage.isInternalEstimate
                  ? bidPackage.latestVersion.latestBidContractRevisions.nonEmpty
                  : (bidPackage.isUnitBased ? requestsWithEditableBids : requestsWithoutContracts).length === 0) &&
                  "Bids cannot be uploaded")
              }
              onClick={onCreateBid}
            />
          </div>
        )}
      </div>
      <GridTable columns={columns} filter={searchFilter} rows={rows} rowStyles={rowStyles} />
    </>
  );
}

const cellBorderStyles = {
  // add border to split trade partner columns
  ...Css.addIn("& > div:nth-of-type(n + 7)", Css.bl.bcGray200.$).$,
};

const rowStyles: RowStyles<Row> = {
  header: { rowCss: cellBorderStyles },
  option: { rowCss: cellBorderStyles },
  tliv: { rowCss: cellBorderStyles },
};

function createRows(
  bidPackage: PageBidPackageDetailFragment,
  bidPackageVersion: PlanBasedBidsTable_BidPackageVersionFragment,
  isDraft: boolean,
  scopeLines: PlanBasedBidsTable_ScopeLinesFragment[],
): GridDataRow<Row>[] {
  const trades = getBidPackageTradesWithEstimate(bidPackage);
  const tradeIds = trades.map((r) => r.tradePartner.id);
  const itemRows = scopeLines.map(({ takeoffLineItemVersion, changeType, previous }) => ({
    kind: "tliv" as const,
    id: takeoffLineItemVersion.id,
    data: {
      item: takeoffLineItemVersion,
      changeType,
      previous,
      // For each materialized scope line, each trade could have bid at most once on it
      costData: trades.keyBy(
        (bpr) => bpr.tradePartner.id,
        ({ bidContractRevision, tradePartner }) => {
          // If we are in draft mode we want to pull costs from the previous version revisions
          if (isDraft) {
            const previous = bidPackageVersion.previous;
            const previousRevision = bidPackage.isInternalEstimate
              ? previous?.latestBidContractRevisions.first
              : previous?.latestBidContractRevisions.find(
                  (bcr) => bcr.bidContract.tradePartner?.id === tradePartner.id,
                );
            if (bidPackage.isUnitBased) {
              const bcli = previousRevision?.lineItems.find(
                (li) => li.bidItem?.id === takeoffLineItemVersion.bidItem?.id,
              );
              // this is the unit cost on the BCLI
              return isNumber(bcli?.totalCostInCents)
                ? bcli?.totalCostInCents * (takeoffLineItemVersion.quantity || 1)
                : undefined;
            } else {
              /*
                Since we are looking at the previous version to show costs we need to used the identity since there may be a new TLIV
                additionally we want to make sure the options haven't changed between the versions
              */
              const revisionBcli = previousRevision?.lineItems.find(
                (li) =>
                  (li.itemTemplateItem as TakeoffLineItemVersion)?.identity.id === takeoffLineItemVersion.identity.id &&
                  takeoffLineItemVersion.options
                    .map((o) => o.id)
                    .hasSameElements(li.itemTemplateItem?.options.map((o) => o.id) ?? []),
              );
              // These are plan based tli therefore the total cost
              return revisionBcli?.totalCostInCents;
            }
          }
          // if bpr.bidcontract.type is unit based then use the bid contract line item total cost
          if (bidContractRevision?.bidContract?.type?.code === BidContractType.UnitBased) {
            const bcli = bidContractRevision.lineItems.find(
              (li) => li.bidItem?.id === takeoffLineItemVersion.bidItem?.id,
            );
            // this is the unit cost on the BCLI
            return isNumber(bcli?.totalCostInCents)
              ? bcli?.totalCostInCents * (takeoffLineItemVersion.quantity || 1)
              : undefined;
          }
          // for prorated bids we would want to use the itemtemplateitem cost in cents
          const revisionBcli = bidContractRevision?.lineItems.find(
            (li) => li.itemTemplateItem?.id === takeoffLineItemVersion.id,
          );
          // These are plan based tli therefore the total cost
          return revisionBcli?.totalCostInCents;
        },
      ),
    },
  }));

  const optionRows = itemRows
    .groupByObject((i) => getOptionsKey(i.data.item.options) || "base")
    .map(([key, children]) => {
      const { item } = children[0].data;
      return {
        kind: "option" as const,
        id: key,
        data: {
          options: item.options,
          costData: children
            // If we are showing removed items, don't sum up their costs
            .filter((c) => c.data.changeType !== AggregateMemberVersionChangeType.REMOVED)
            .map((c) => c.data.costData)
            .reduce(sumTradeCostData.bind(null, tradeIds), {}),
        },
        children,
      };
    });

  const headerWithCost = {
    ...simpleHeader,
    data: { costData: optionRows.map((r) => r.data.costData).reduce(sumTradeCostData.bind(null, tradeIds), {}) },
  };

  return [headerWithCost, ...optionRows];
}

type HeaderRow = { kind: "header"; data: { costData: TradeCostData } };
type OptionRow = {
  kind: "option";
  data: { options: PlanBasedBidsTable_ReadyPlanOptionFragment[]; costData: TradeCostData };
};
type TLIVRow = {
  kind: "tliv";
  data: {
    item: PlanBasedBidsTable_TakeoffLineItemVersionFragment;
    changeType: Maybe<string>;
    previous: Maybe<PlanBasedBidsTable_PreviousTakeoffLineItemVersionFragment>;
    costData: TradeCostData;
  };
};

type Row = HeaderRow | OptionRow | TLIVRow;

function createColumns(
  bidPackage: PageBidPackageDetailFragment,
  onEditProratedBid: (bidContractRevisionId: string) => void,
  onUploadProratedBid: (tradePartnerId: string) => void,
  isDraft: boolean,
  displayingOldVersion: boolean,
  optionsWithMultipleGlobalOptions: string[],
): GridColumn<Row>[] {
  const tradeColumns = getBidPackageTradesWithEstimate(bidPackage);
  return [
    collapseColumn<Row>({
      tliv: (data) => ({
        content: emptyCell.content,
        css: isDraft ? applyStyling(data) : undefined,
      }),
    }),
    column<Row>({
      header: "Code",
      option: emptyCell,
      tliv: (data) => ({
        content: <DropCodeCell scope={data.item} />,
        css: isDraft ? applyStyling(data, "drop") : undefined,
      }),
      w: "90px",
    }),
    column<Row>({
      header: "Name",
      option: ({ options }) => ({
        content:
          options
            .map((o) => (optionsWithMultipleGlobalOptions.includes(o.id) ? `${o.name} - ${o.location?.name}` : o.name))
            .join(", ") || "Base",
        css: Css.smSb.$,
      }),
      tliv: (data) => ({
        content: [data.item.name, data.item.bidItem?.parentMaterialVariant?.code].compact().join(" "),
        css: isDraft ? applyStyling(data, "materialVariant") : undefined,
      }),
    }),
    column<Row>({
      header: "Qty",
      option: emptyCell,
      tliv: (data) => ({
        content: data.item.quantity,
        css: isDraft ? applyStyling(data, "quantity") : undefined,
      }),
      w: "53px",
    }),
    column<Row>({
      header: "UoM",
      option: emptyCell,
      tliv: (data) => ({
        content: data.item.unitOfMeasure.shortName,
        css: isDraft ? applyStyling(data, "unitOfMeasure") : undefined,
      }),
      w: "67px",
    }),
    ...tradeColumns.map(({ tradePartner, bidContractRevision }) => {
      const isProratedBid =
        bidContractRevision?.bidContract?.type?.code &&
        [BidContractType.GroupedByMaterial, BidContractType.GroupedByPlanAndOption].includes(
          bidContractRevision?.bidContract?.type?.code,
        );

      return numericColumn<Row>({
        header: ({ costData }) => (
          <div css={Css.w100.df.aic.$}>
            <div css={Css.mra.$}>
              {tradePartner.name}
              {costData[tradePartner.id] && (
                <>
                  {" "}
                  (<Price valueInCents={costData[tradePartner.id]} />)
                </>
              )}
            </div>
            <div css={Css.df.gap1.$}>
              {!isDraft && !displayingOldVersion && isProratedBid && !bidPackage.isInternalEstimate && (
                <Tooltip title="Upload your prorated bid" placement="top">
                  <IconButton
                    icon="upload"
                    onClick={() => onUploadProratedBid(tradePartner.id)}
                    disabled={
                      bidContractRevision?.bidContract?.type?.code === BidContractType.GroupedByMaterial &&
                      "Currently not supported for Grouped By Material"
                    }
                    color={Palette.Blue600}
                  />
                </Tooltip>
              )}
              {!isDraft && !displayingOldVersion && isProratedBid && (
                <Tooltip title="Edit your prorated bid" placement="top">
                  <IconButton
                    icon="pencil"
                    onClick={() => onEditProratedBid(bidContractRevision?.id)}
                    color={Palette.Blue600}
                  />
                </Tooltip>
              )}
            </div>
          </div>
        ),
        option: ({ costData }) =>
          isNumber(costData[tradePartner.id]) ? priceCell({ valueInCents: costData[tradePartner.id] }) : emptyCell,
        tliv: (data) => ({
          content: isNumber(data.costData[tradePartner.id]) ? (
            <Price valueInCents={data.costData[tradePartner.id]} />
          ) : (
            emptyCell.content
          ),
          css: isDraft ? applyStyling(data) : undefined,
        }),
      });
    }),
  ];
}

type Field = keyof Pick<
  PlanBasedBidsTable_TakeoffLineItemVersionFragment,
  "options" | "quantity" | "unitOfMeasure" | "materialVariant" | "drop"
>;

function fieldHasChanged(data: TLIVRow["data"], field?: Field): boolean {
  if (!data.previous || !field) return false;
  if (field === "quantity") {
    return data.previous.quantity !== data.item.quantity;
  } else if (field === "options") {
    return optionsHaveChanged(data);
  }
  const previous = data.previous[field]?.id;
  const current = data.item[field]?.id;
  return previous !== current;
}

function applyStyling(data: TLIVRow["data"], field?: Field) {
  // We want to highlight the whole row if either the row change type is added or if the options on the row have changed
  if (data.changeType === AggregateMemberVersionChangeType.ADDED || fieldHasChanged(data, "options")) {
    return Css.bgYellow50.$;
  }
  if (data.changeType === AggregateMemberVersionChangeType.REMOVED) {
    return Css.bgGray50.tdlt.gray500.$;
  }
  if (data.changeType === AggregateMemberVersionChangeType.UPDATED && fieldHasChanged(data, field)) {
    return Css.bgYellow50.$;
  }
}

const allFields: Field[] = ["drop", "quantity", "unitOfMeasure", "materialVariant", "options"];

function rowHasChanges(data: TLIVRow["data"]): boolean {
  if (data.changeType === AggregateMemberVersionChangeType.ADDED) return true;
  if (data.changeType === AggregateMemberVersionChangeType.REMOVED) return true;
  if (fieldHasChanged(data, "options")) return true;
  if (data.changeType === AggregateMemberVersionChangeType.UPDATED) {
    return allFields.some((f) => fieldHasChanged(data, f));
  }
  return false;
}

function filterToChangedRows(rows: GridDataRow<Row>[]): GridDataRow<Row>[] {
  return rows.flatMap((row) => {
    if (row.kind === "header") {
      return [row] as GridDataRow<Row>[];
    } else if (row.kind === "option") {
      // Look down into the tlivs for changes, and only return plan/parent rows with matching children
      const filteredTlivs = (row.children ?? []).filter(
        (tlivRow) => tlivRow.kind === "tliv" && rowHasChanges(tlivRow.data),
      );
      if (filteredTlivs.length === 0) return [];
      return [{ ...row, children: filteredTlivs }];
    } else {
      return [row] as GridDataRow<Row>[];
    }
  });
}

function optionsHaveChanged(data: TLIVRow["data"]) {
  const prev = data.previous;
  if (!prev || data.changeType !== AggregateMemberVersionChangeType.UPDATED) return false;
  return xor(
    prev.options?.map((o) => o.id),
    data.item.options?.map((o) => o.id),
  ).nonEmpty;
}
